import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiAlertTriangle, FiArrowRight } from "react-icons/fi";
import { useAuth } from "../../hooks/useAuth";
import { localizedPath } from "../../hooks/useLocalizedNavigate";
import { czyKafelekDostepny, getTileBySlug } from "../../constants/adminTilesConfig";
import useTrybTestowy from "./useTrybTestowy";
import s from "./BanerTrybuTestowego.module.css";

/**
 * BANER „TRYB TESTOWY POWIADOMIEŃ” — nad nagłówkiem aplikacji (polecenie właściciela 2026-10-02).
 *
 * Właściciel: każdy, kogo tryb dotyczy, ma widzieć nad nagłówkiem, że powiadomienia do nikogo nie trafią.
 *
 * KTO WIDZI: każde zalogowane konto, którego spółka jest w trybie (`moj.aktywny` z serwera) albo które wysyła
 * do spółki w trybie (`inneSpolkiWTrybie`). W trybie podglądu konta `useAuth` odpowiada już jako osoba
 * oglądana, więc administrator widzi dokładnie to, co pracownik.
 *
 * [poprawka 2026-10-04, zgłoszenie właściciela: „włączyłem tryb testowy dla firmy, dlaczego nie ma paska”]
 * BYŁO: osoba z dostępem do kafelka „Microsoft 365 — integracje i skrzynki” baneru NIE widziała — miała tylko
 * komunikat na stronie /admin. Skutek: ten, kto tryb włączył, przechodził na dowolny inny ekran i nic mu nie
 * mówiło, że tryb trwa; właściciel uznał, że paska nie ma wcale. TERAZ ta osoba widzi ten sam baner co wszyscy,
 * z dopiskiem i przyciskiem „Wyłącz w Integracjach” (prowadzi do sekcji skrzynek). Pracownik bez dostępu do
 * integracji przycisku nie ma — nie miałby dokąd pójść. Komunikat na /admin zostaje.
 *
 * PRACOWNIK INNEJ SPÓŁKI (poprawka po przeglądzie): reguła wysyłki przekierowuje całą wiadomość, która dotyka
 * spółki w trybie — także wtedy, gdy z tej spółki jest tylko adresat (np. akceptujący wniosek). Serwer oddaje
 * wtedy `aktywny=false` i nazwy spółek w trybie (`inneSpolkiWTrybie`); baner mówi wprost, że powiadomienia do
 * osób z tych spółek nie trafią. Zdania o wnioskach tu nie ma — własne sprawy tej osoby biegną normalnie.
 *
 * BEZ ✕ (dla wszystkich, także dla osoby z przyciskiem) — to nie jest pasek wyjaśniający ekran, tylko STAN aplikacji: zamknięty baner oznaczałby, że człowiek
 * wysyła wniosek w przekonaniu, że ktoś dostanie o nim wiadomość. Znika sam, gdy tryb zostanie wyłączony
 * (zdarzenie huba → `useTrybTestowy`).
 *
 * Błąd odczytu albo brak odpowiedzi = nic na ekranie.
 */
export const tekstBaneruTrybuTestowego = (spolkaNazwa) => ({
    etykieta: `TRYB TESTOWY POWIADOMIEŃ · ${spolkaNazwa || "cała aplikacja"}`,
    zdanie1: "Twoje powiadomienia do nikogo nie trafią: e-maile z aplikacji trafiają do skrzynki testowej, SMS-y są wstrzymane (powiadomienia w aplikacji działają).",
    zdanie2: "Aplikacja nie uwzględnia złożonych wniosków oraz zgłoszeń w realizacji.",
});

/** Baner dla pracownika spółki, która sama NIE jest w trybie — o spółkach, które w nim są. */
export const tekstBaneruInnychSpolek = (nazwy) => ({
    etykieta: `TRYB TESTOWY POWIADOMIEŃ · ${nazwy.join(", ")}`,
    zdanie1: nazwy.length === 1
        ? "Powiadomienia, które wysyłasz do osób z tej spółki, nie dotrą: e-maile z aplikacji trafiają do skrzynki testowej, SMS-y są wstrzymane (powiadomienia w aplikacji działają)."
        : "Powiadomienia, które wysyłasz do osób z tych spółek, nie dotrą: e-maile z aplikacji trafiają do skrzynki testowej, SMS-y są wstrzymane (powiadomienia w aplikacji działają).",
    zdanie2: null,
});

/** Co baner ma powiedzieć na podstawie odpowiedzi serwera — albo `null`, gdy nic. */
export const trescBaneruTrybuTestowego = (moj) => {
    if (!moj) return null;
    if (moj.aktywny === true) return tekstBaneruTrybuTestowego(moj.spolkaNazwa);
    const inne = Array.isArray(moj.inneSpolkiWTrybie)
        ? moj.inneSpolkiWTrybie.filter((n) => typeof n === "string" && n.trim() !== "")
        : [];
    return inne.length > 0 ? tekstBaneruInnychSpolek(inne) : null;
};

/** Dopisek dla osoby, która może tryb wyłączyć — stoi po zdaniach właściciela, przed przyciskiem. */
export const DOPISEK_DLA_OSOBY_Z_INTEGRACJAMI = "Możesz to wyłączyć.";

const BanerTrybuTestowego = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { moj } = useTrybTestowy();

    const t = trescBaneruTrybuTestowego(moj);
    if (!t) return null;
    // Ten sam predykat, którym /admin rysuje kafelek integracji — przycisk nie może prowadzić do odmowy.
    const maIntegracje = czyKafelekDostepny(getTileBySlug("integrations"), auth);

    return (
        <div className={s.tryb} role="status" data-testid="baner-trybu-testowego">
            <span className={s.ikona} aria-hidden="true"><FiAlertTriangle size={16} /></span>
            <span className={s.tresc}>
                <strong className={s.etykieta}>{t.etykieta}</strong>
                {" — "}
                {t.zdanie1}
                {t.zdanie2 ? ` ${t.zdanie2}` : null}
                {maIntegracje ? ` ${DOPISEK_DLA_OSOBY_Z_INTEGRACJAMI}` : null}
            </span>
            {maIntegracje && (
                <button
                    type="button"
                    className={s.akcja}
                    data-testid="baner-trybu-testowego-wylacz"
                    onClick={() => navigate(`${localizedPath("admin")}/integrations`, {
                        state: { origin: location?.pathname || localizedPath("admin"), section: "skrzynki" },
                    })}
                >
                    Wyłącz w Integracjach <FiArrowRight size={14} aria-hidden="true" />
                </button>
            )}
        </div>
    );
};

export default BanerTrybuTestowego;
